import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'palette/Button';
import { getCardTranslations } from '@common/Favorites/helpers/cardTranslations';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { Product } from '@common/Listing/types';
import { MyListsCard } from '@common/MyLists/containers/MyListsCard';
import { openRecents } from '@common/MyLists/store/actions/MyListsActions';
import { ItemsParameters, ItemInteractionOrigin } from '@common/Product/AddToFavorites/types';
import { trackProductImpressions } from '@common/Tracking';
import { getStoreEnv } from '@helpers/env';

interface RecentProductsProps {
    products: Product[];
    pageSize: number;
    className: string;
    isRightSticky: boolean;
    itemsParameters: ItemsParameters;
    interactionOrigin: ItemInteractionOrigin;
}

export function RecentsProducts({
    products,
    pageSize,
    className,
    isRightSticky,
    itemsParameters,
    interactionOrigin,
}: RecentProductsProps) {
    const dispatch = useDispatch();

    const config = useConfig();
    const translate = useTranslation();

    const productsContainerRef = useRef<HTMLUListElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | undefined>();

    const myPageSize = pageSize || products.length;

    const openRecentsClick = () => {
        dispatch(openRecents());
    };

    useEffect(() => {
        if (['booztmobile', 'boozt'].indexOf(getStoreEnv(config)) !== -1 && productsContainerRef) {
            trackProductImpressions(productsContainerRef.current);
            setContainerWidth(productsContainerRef.current?.offsetWidth);
        }
    }, [config]);

    return (
        <ul
            ref={productsContainerRef}
            className={classNames(
                className,
                'product-tabs__list product-tabs__list--recently',
                'product-tabs__list--product-card-redesign',
                {
                    'product-tabs__list--empty': products.length === 0,
                },
            )}
            data-current-page={1}
            data-products-group={itemsParameters.item_list_name}
            data-products-per-page={myPageSize}
            data-total-products={products.length}
        >
            {products.length > 0 ? (
                products
                    .slice(0, 1 * myPageSize)
                    .map((product, index: number) => {
                        const bmpTrackingData = {
                            totalProducts: products.length,
                            productsPerPage: myPageSize,
                            currentPage: 1,
                            itemIndex: index,
                            group: itemsParameters.item_list_name,
                            containerOffsetWidth: containerWidth,
                        };
                        return (
                            <MyListsCard
                                key={`${product?.variants?.length > 0 ? product.variants[0].id : product?.id}
                                _${isRightSticky ? 'RFListingRightSticky'
                                : 'RFListingLeftSticky'}`}
                                cardStrings={getCardTranslations(translate)}
                                containerClassName={classNames(
                                    'product-mini-list__item',
                                    'product-mini-list__item--product-card-redesign',
                                )}
                                product={product}
                                trackingValues={{
                                    list: itemsParameters.item_list_name,
                                    bmp: bmpTrackingData,
                                    itemsParameters,
                                    interactionOrigin,
                                }}
                                type={isRightSticky ? 'RFListingRightSticky' : 'RFListingLeftSticky'}
                            />
                        );
                    })
            ) : (
                <li className="message-empty">{translate('customer_my_lists_recently_empty')}</li>
            )}
            {products.length > myPageSize
                && (
                    <li
                        className="view-all"
                    >
                        <Button
                            isExpanded
                            onClick={openRecentsClick}
                        >
                            {translate('helper.view_all.label')}
                        </Button>
                    </li>
                )}
        </ul>
    );
}
